/*
 * Carrusel de imágenes para la sección «Imagen con texto superpuesto».
 *
 * La sección apila varias <picture> dentro del mismo .content-over-media del tema: como el tema ya coloca toda imagen
 * hija en la misma celda de la rejilla («full-start» a «full-end»), las imágenes quedan una encima de otra sin añadir
 * ni un contenedor. Este elemento solo decide cuál se ve: cambia la clase .is-selected —y, en el modo «deslizar», el
 * translateX de cada una—, enciende el enlace de la imagen que toca y lleva las flechas y los puntos, que viven
 * dentro de él.
 *
 * Sin JavaScript la sección sigue siendo la de siempre: la primera imagen ya viene con .is-selected desde el servidor
 * y el resto se quedan ocultas por CSS, así que nunca se ve una sección en blanco.
 *
 * Coste en reposo: cero. El avance automático solo existe mientras la sección está a la vista, la pestaña en primer
 * plano y el sistema no pide menos animación; si la clienta arrastra, toca, teclea o deja el ratón encima, manda ella.
 */
(function () {
  if (customElements.get('image-overlay-carousel')) return;

  var REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');
  var RESUME_DELAY = 5000;
  // Píxeles de arrastre horizontal a partir de los cuales el gesto cuenta como pasar de imagen.
  var SWIPE_THRESHOLD = 40;
  var PAUSE_EVENTS = ['keydown', 'focusin'];

  class ImageOverlayCarousel extends HTMLElement {
    connectedCallback() {
      this.media = this.closest('.content-over-media');

      if (!this.media) return;

      this.slides = Array.prototype.slice.call(this.media.querySelectorAll('.overlay-carousel__slide'));
      this.links = Array.prototype.slice.call(this.media.querySelectorAll('.overlay-carousel__link'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-carousel-dot]'));

      // Con una sola imagen no hay carrusel que gobernar: se queda tal cual la pintó el servidor.
      if (this.slides.length < 2) return;

      this.effect = this.getAttribute('effect') === 'slide' ? 'slide' : 'fade';
      this.rtl = getComputedStyle(this.media).direction === 'rtl';
      this.index = 0;

      this.onClick = this.onClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);

      this.addEventListener('click', this.onClick);
      this.media.addEventListener('pointerdown', this.onPointerDown);
      this.media.addEventListener('pointerup', this.onPointerUp);
      this.media.addEventListener('pointercancel', this.onPointerUp);
      // Un arrastre no debe abrir el enlace de la imagen: se anula el clic que el navegador emite tras el gesto.
      this.media.addEventListener('click', this.onClickCapture, true);

      this.apply(true);
      this.setupAutoplay();

      // En el editor, abrir un bloque de imagen enseña esa imagen (y detiene el avance mientras está abierto).
      if (window.Shopify && window.Shopify.designMode) {
        this.section = this.closest('.shopify-section');

        if (this.section) {
          this.onBlockToggle = this.onBlockToggle.bind(this);
          this.section.addEventListener('shopify:block:select', this.onBlockToggle);
          this.section.addEventListener('shopify:block:deselect', this.onBlockToggle);
        }
      }
    }

    disconnectedCallback() {
      if (!this.media || this.slides == null || this.slides.length < 2) return;

      this.removeEventListener('click', this.onClick);
      this.media.removeEventListener('pointerdown', this.onPointerDown);
      this.media.removeEventListener('pointerup', this.onPointerUp);
      this.media.removeEventListener('pointercancel', this.onPointerUp);
      this.media.removeEventListener('click', this.onClickCapture, true);
      this.teardownAutoplay();

      if (this.section) {
        this.section.removeEventListener('shopify:block:select', this.onBlockToggle);
        this.section.removeEventListener('shopify:block:deselect', this.onBlockToggle);
      }
    }

    /* ── Navegación ─────────────────────────────────────────────────────────────────────────────────────────── */

    select(index) {
      var count = this.slides.length;
      var next = ((index % count) + count) % count;

      if (next === this.index) return;

      this.index = next;
      this.apply(false);
    }

    next() {
      this.select(this.index + 1);
    }

    previous() {
      this.select(this.index - 1);
    }

    /* Único sitio donde se toca el DOM: reparte .is-selected, desplazamientos, enlaces y puntos. */
    apply(initial) {
      var count = this.slides.length;
      var direction = this.rtl ? -1 : 1;

      // La primera imagen la anima el tema al entrar en pantalla y deja estilos en línea; en cuanto el carrusel se
      // mueve por primera vez se cancela esa animación y se limpia lo que dejó, o esa imagen se quedaría encima de
      // las demás para siempre.
      if (!initial && !this.cleaned) {
        this.cleaned = true;

        this.slides.forEach((slide) => {
          if (slide.getAnimations) slide.getAnimations().forEach((animation) => animation.cancel());

          slide.style.opacity = '';
          slide.style.transform = '';
          slide.style.scale = '';
        });
      }

      this.slides.forEach((slide, i) => {
        var offset = i - this.index;

        // Se va siempre por el camino corto, para que del final al principio no desfilen todas las imágenes.
        if (offset > count / 2) offset -= count;
        if (offset < count / -2) offset += count;

        slide.classList.toggle('is-selected', i === this.index);

        if (this.effect === 'slide') {
          var previous = parseFloat(slide.dataset.offset);
          // Al saltar más de una posición la imagen está fuera de la vista: se recoloca sin animar para que no cruce
          // la pantalla de lado a lado.
          var jumps = initial || isNaN(previous) || Math.abs(offset - previous) > 1;

          if (jumps) slide.style.transition = 'none';
          slide.style.transform = 'translateX(' + offset * direction * 100 + '%)';
          if (jumps) {
            void slide.offsetWidth;
            slide.style.transition = '';
          }

          slide.dataset.offset = offset;
        }
      });

      // Los enlaces van por número de imagen, porque solo se pintan los de las imágenes que tienen destino.
      this.links.forEach((link) => link.classList.toggle('is-selected', parseInt(link.dataset.slide, 10) === this.index));
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    }

    /* ── Controles ──────────────────────────────────────────────────────────────────────────────────────────── */

    onClick(event) {
      var control = event.target.closest('[data-carousel-action], [data-carousel-dot]');

      if (!control || !this.contains(control)) return;

      var action = control.getAttribute('data-carousel-action');

      if (action === 'next') {
        this.next();
      } else if (action === 'previous') {
        this.previous();
      } else {
        this.select(this.dots.indexOf(control));
      }

      this.pause();
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.pointer = { x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      var start = this.pointer;

      this.pointer = null;

      if (!start || event.type === 'pointercancel') return;

      var deltaX = event.clientX - start.x;
      var deltaY = event.clientY - start.y;

      // Solo cuenta como gesto lo que es claramente horizontal: lo vertical es la clienta desplazando la página.
      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      this.swiped = true;
      setTimeout(() => (this.swiped = false), 0);

      if (deltaX * (this.rtl ? -1 : 1) < 0) {
        this.next();
      } else {
        this.previous();
      }

      this.pause();
    }

    onClickCapture(event) {
      if (!this.swiped) return;

      event.preventDefault();
      event.stopPropagation();
    }

    /* ── Avance automático ──────────────────────────────────────────────────────────────────────────────────── */

    setupAutoplay() {
      var seconds = parseFloat(this.getAttribute('interval'));

      if (!(seconds > 0)) return;

      this.delay = Math.max(1, seconds) * 1000;
      this.timer = this.resumeTimer = 0;
      this.onScreen = false;
      // Ratón encima, interacción reciente o bloque abierto en el editor.
      this.blocked = false;

      this.sync = this.sync.bind(this);
      this.pause = this.pause.bind(this);
      this.onPointerHover = this.onPointerHover.bind(this);

      REDUCED_MOTION.addEventListener('change', this.sync);
      document.addEventListener('visibilitychange', this.sync);
      PAUSE_EVENTS.forEach((type) => this.media.addEventListener(type, this.pause, { passive: true }));
      this.media.addEventListener('pointerenter', this.onPointerHover);
      this.media.addEventListener('pointerleave', this.onPointerHover);

      // observe() dispara una primera entrada con el estado actual, así que este observer hace también el arranque.
      this.observer = new IntersectionObserver((entries) => {
        this.onScreen = entries[entries.length - 1].isIntersecting;
        this.sync();
      });
      this.observer.observe(this.media);
    }

    teardownAutoplay() {
      if (!this.delay) return;

      this.stop();
      clearTimeout(this.resumeTimer);
      REDUCED_MOTION.removeEventListener('change', this.sync);
      document.removeEventListener('visibilitychange', this.sync);
      PAUSE_EVENTS.forEach((type) => this.media.removeEventListener(type, this.pause));
      this.media.removeEventListener('pointerenter', this.onPointerHover);
      this.media.removeEventListener('pointerleave', this.onPointerHover);
      this.observer.disconnect();
    }

    /* Única puerta de entrada: decide si el temporizador debe existir. */
    sync() {
      if (this.onScreen && !this.blocked && !REDUCED_MOTION.matches && !document.hidden) {
        if (!this.timer) this.timer = setInterval(() => this.next(), this.delay);
      } else {
        this.stop();
      }
    }

    stop() {
      this.timer = clearInterval(this.timer);
    }

    /* Cualquier gesto de la clienta manda: paramos y volvemos a intentarlo pasado RESUME_DELAY. */
    pause() {
      if (!this.delay) return;

      this.blocked = true;
      this.stop();
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.blocked = false;
        this.sync();
      }, RESUME_DELAY);
    }

    /* Con el ratón encima el carrusel se queda quieto todo el rato, no solo unos segundos. */
    onPointerHover(event) {
      if (event.pointerType !== 'mouse') return;

      this.blocked = event.type === 'pointerenter';
      clearTimeout(this.resumeTimer);
      this.sync();
    }

    /* Editor: con un bloque de imagen abierto el carrusel enseña esa imagen y no se mueve hasta que se cierra. */
    onBlockToggle(event) {
      var selected = this.slides.findIndex((slide) => {
        var picture = slide.closest('[data-block-id]');

        return picture && picture.dataset.blockId === event.detail.blockId;
      });

      if (selected < 0) return;

      if (event.type === 'shopify:block:select') this.select(selected);

      if (!this.delay) return;

      this.blocked = event.type === 'shopify:block:select';
      clearTimeout(this.resumeTimer);
      this.sync();
    }
  }

  customElements.define('image-overlay-carousel', ImageOverlayCarousel);
})();
